var __extends = this && this.__extends || function () {
  var _extendStatics = function extendStatics(d, b) {
    _extendStatics = Object.setPrototypeOf || {
      __proto__: []
    } instanceof Array && function (d, b) {
      d.__proto__ = b;
    } || function (d, b) {
      for (var p in b) {
        if (b.hasOwnProperty(p)) d[p] = b[p];
      }
    };

    return _extendStatics(d, b);
  };

  return function (d, b) {
    _extendStatics(d, b);

    function __() {
      this.constructor = d;
    }

    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
  };
}();

var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];

      for (var p in s) {
        if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
      }
    }

    return t;
  };

  return __assign.apply(this, arguments);
};

import './fixPolyfills';
import { ApolloCache } from 'apollo-cache';
import { getFragmentQueryDocument, addTypenameToDocument } from 'apollo-utilities';
import { HeuristicFragmentMatcher } from './fragmentMatcher';
import { StoreReader } from './readFromStore';
import { StoreWriter } from './writeToStore';
import { DepTrackingCache } from './depTrackingCache';
import { wrap, CacheKeyNode } from './optimism';
import { ObjectCache } from './objectCache';
import { record } from './recordingCache';
var defaultConfig = {
  fragmentMatcher: new HeuristicFragmentMatcher(),
  dataIdFromObject: defaultDataIdFromObject,
  addTypename: true,
  resultCaching: true
};
export function defaultDataIdFromObject(result) {
  if (result.__typename) {
    if (result.id !== undefined) {
      return result.__typename + ":" + result.id;
    }

    if (result._id !== undefined) {
      return result.__typename + ":" + result._id;
    }
  }

  return null;
}

var InMemoryCache = function (_super) {
  __extends(InMemoryCache, _super);

  function InMemoryCache(config) {
    if (config === void 0) {
      config = {};
    }

    var _this = _super.call(this) || this;

    _this.optimistic = [];
    _this.watches = new Set();
    _this.typenameDocumentCache = new Map();
    _this.cacheKeyRoot = new CacheKeyNode();
    _this.silenceBroadcast = false;
    _this.config = __assign({}, defaultConfig, config);

    if (_this.config.customResolvers) {
      console.warn('customResolvers have been renamed to cacheRedirects. Please update your config as we will be deprecating customResolvers in the next major version.');
      _this.config.cacheRedirects = _this.config.customResolvers;
    }

    if (_this.config.cacheResolvers) {
      console.warn('cacheResolvers have been renamed to cacheRedirects. Please update your config as we will be deprecating cacheResolvers in the next major version.');
      _this.config.cacheRedirects = _this.config.cacheResolvers;
    }

    _this.addTypename = _this.config.addTypename;
    _this.data = _this.config.resultCaching ? new DepTrackingCache() : new ObjectCache();
    _this.storeReader = new StoreReader(_this.cacheKeyRoot);
    _this.storeWriter = new StoreWriter();
    var cache = _this;
    var maybeBroadcastWatch = cache.maybeBroadcastWatch;
    _this.maybeBroadcastWatch = wrap(function (c) {
      return maybeBroadcastWatch.call(_this, c);
    }, {
      makeCacheKey: function makeCacheKey(c) {
        if (c.optimistic && cache.optimistic.length > 0) {
          return;
        }

        if (c.previousResult) {
          return;
        }

        if (cache.data instanceof DepTrackingCache) {
          return cache.cacheKeyRoot.lookup(c.query, JSON.stringify(c.variables));
        }
      }
    });
    return _this;
  }

  InMemoryCache.prototype.restore = function (data) {
    if (data) this.data.replace(data);
    return this;
  };

  InMemoryCache.prototype.extract = function (optimistic) {
    if (optimistic === void 0) {
      optimistic = false;
    }

    if (optimistic && this.optimistic.length > 0) {
      var patches = this.optimistic.map(function (opt) {
        return opt.data;
      });
      return Object.assign.apply(Object, [{}, this.data.toObject()].concat(patches));
    }

    return this.data.toObject();
  };

  InMemoryCache.prototype.read = function (query) {
    if (query.rootId && this.data.get(query.rootId) === undefined) {
      return null;
    }

    var store = query.optimistic && this.optimistic.length ? new ObjectCache(this.extract(true)) : this.data;
    return this.storeReader.readQueryFromStore({
      store: store,
      query: this.transformDocument(query.query),
      variables: query.variables,
      rootId: query.rootId,
      fragmentMatcherFunction: this.config.fragmentMatcher.match,
      previousResult: query.previousResult,
      config: this.config
    });
  };

  InMemoryCache.prototype.write = function (write) {
    this.storeWriter.writeResultToStore({
      dataId: write.dataId,
      result: write.result,
      variables: write.variables,
      document: this.transformDocument(write.query),
      store: this.data,
      dataIdFromObject: this.config.dataIdFromObject,
      fragmentMatcherFunction: this.config.fragmentMatcher.match
    });
    this.broadcastWatches();
  };

  InMemoryCache.prototype.diff = function (query) {
    var store = query.optimistic && this.optimistic.length ? new ObjectCache(this.extract(true)) : this.data;
    return this.storeReader.diffQueryAgainstStore({
      store: store,
      query: this.transformDocument(query.query),
      variables: query.variables,
      returnPartialData: query.returnPartialData,
      previousResult: query.previousResult,
      fragmentMatcherFunction: this.config.fragmentMatcher.match,
      config: this.config
    });
  };

  InMemoryCache.prototype.watch = function (watch) {
    var _this = this;

    this.watches.add(watch);
    return function () {
      _this.watches.delete(watch);
    };
  };

  InMemoryCache.prototype.evict = function (query) {
    throw new Error("eviction is not implemented on InMemory Cache");
  };

  InMemoryCache.prototype.reset = function () {
    this.data.clear();
    this.broadcastWatches();
    return Promise.resolve();
  };

  InMemoryCache.prototype.removeOptimistic = function (id) {
    var _this = this;

    var toPerform = this.optimistic.filter(function (item) {
      return item.id !== id;
    });
    this.optimistic = [];
    toPerform.forEach(function (change) {
      _this.recordOptimisticTransaction(change.transaction, change.id);
    });
    this.broadcastWatches();
  };

  InMemoryCache.prototype.performTransaction = function (transaction) {
    var alreadySilenced = this.silenceBroadcast;
    this.silenceBroadcast = true;
    transaction(this);

    if (!alreadySilenced) {
      this.silenceBroadcast = false;
    }

    this.broadcastWatches();
  };

  InMemoryCache.prototype.recordOptimisticTransaction = function (transaction, id) {
    var _this = this;

    this.silenceBroadcast = true;
    var patch = record(this.extract(true), function (recordingCache) {
      var dataCache = _this.data;
      _this.data = recordingCache;

      _this.performTransaction(transaction);

      _this.data = dataCache;
    });
    this.optimistic.push({
      id: id,
      transaction: transaction,
      data: patch
    });
    this.silenceBroadcast = false;
    this.broadcastWatches();
  };

  InMemoryCache.prototype.transformDocument = function (document) {
    if (this.addTypename) {
      var result = this.typenameDocumentCache.get(document);

      if (!result) {
        result = addTypenameToDocument(document);
        this.typenameDocumentCache.set(document, result);
        this.typenameDocumentCache.set(result, result);
      }

      return result;
    }

    return document;
  };

  InMemoryCache.prototype.readQuery = function (options, optimistic) {
    if (optimistic === void 0) {
      optimistic = false;
    }

    return this.read({
      query: options.query,
      variables: options.variables,
      optimistic: optimistic
    });
  };

  InMemoryCache.prototype.readFragment = function (options, optimistic) {
    if (optimistic === void 0) {
      optimistic = false;
    }

    return this.read({
      query: this.transformDocument(getFragmentQueryDocument(options.fragment, options.fragmentName)),
      variables: options.variables,
      rootId: options.id,
      optimistic: optimistic
    });
  };

  InMemoryCache.prototype.writeQuery = function (options) {
    this.write({
      dataId: 'ROOT_QUERY',
      result: options.data,
      query: this.transformDocument(options.query),
      variables: options.variables
    });
  };

  InMemoryCache.prototype.writeFragment = function (options) {
    this.write({
      dataId: options.id,
      result: options.data,
      query: this.transformDocument(getFragmentQueryDocument(options.fragment, options.fragmentName)),
      variables: options.variables
    });
  };

  InMemoryCache.prototype.broadcastWatches = function () {
    var _this = this;

    if (!this.silenceBroadcast) {
      var optimistic_1 = this.optimistic.length > 0;
      this.watches.forEach(function (c) {
        _this.maybeBroadcastWatch(c);

        if (optimistic_1) {
          _this.maybeBroadcastWatch.dirty(c);
        }
      });
    }
  };

  InMemoryCache.prototype.maybeBroadcastWatch = function (c) {
    c.callback(this.diff({
      query: c.query,
      variables: c.variables,
      previousResult: c.previousResult && c.previousResult(),
      optimistic: c.optimistic
    }));
  };

  return InMemoryCache;
}(ApolloCache);

export { InMemoryCache };